import {AxiosError, AxiosResponse} from "axios";
import {injectable} from "inversify";

import "@/extensions/date.extensions";
import {ApiService} from "@/services/apiService";
import {IActivityOccurrenceService} from "@/injection/interfaces";
import {Activity, ActivityRegistration, ActivitySchedule, Participant} from "@/types/entities/activity";
import {SucceededOrNotResponse} from "@/types/responses";
import {ActivityOccurrence} from "@/types/entities/activity/activityOccurrence";

@injectable()
export class ActivityOccurrenceService extends ApiService implements IActivityOccurrenceService {
  public async getActivityOccurrence(id: string): Promise<ActivityOccurrence> {
    const response = await this._httpClient
      .get<string, AxiosResponse<ActivityOccurrence>>(`${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences/${id}`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data as ActivityOccurrence;

  }

  public async getFutureActivityOccurrences(): Promise<ActivityOccurrence[]> {
    const response = await this._httpClient
      .get<ActivityOccurrence[], AxiosResponse<ActivityOccurrence[]>>(`${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences/future`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data as ActivityOccurrence[];
  }

  public async getPastActivityOccurrences(): Promise<ActivityOccurrence[]> {
    const response = await this._httpClient
      .get<ActivityOccurrence[], AxiosResponse<ActivityOccurrence[]>>(`${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences/past`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data as ActivityOccurrence[];
  }

  public async createActivityOccurrence(activityId: string, dates: ActivitySchedule): Promise<ActivityOccurrence> {
    const response = await this._httpClient
      .post<CreateActivityOccurrenceRequestBody, AxiosResponse<ActivityOccurrence>>(
        `${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences`,
        new CreateActivityOccurrenceRequestBody(activityId, dates),
        this.headersWithJsonContentType())
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data as ActivityOccurrence;
  }

  public async deleteActivityOccurrence(id: string): Promise<SucceededOrNotResponse> {
    const response = await this._httpClient
      .delete<string, AxiosResponse<SucceededOrNotResponse>>(`${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences/${id}`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return new SucceededOrNotResponse(response.status === 204)
  }

  public async getActivityOccurrenceParticipant(participantId: string): Promise<Participant> {
    const response = await this._httpClient
      .get<string, AxiosResponse<Participant>>(`${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences/participants/${participantId}`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data as Participant;
  }

  public async getActivityOccurrenceParticipants(activityOccurrenceId: string): Promise<Participant[]> {
    const response = await this._httpClient
      .get<string, AxiosResponse<Participant[]>>(`${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences/${activityOccurrenceId}/participants`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data as Participant[];
  }

  public async addParticipantToActivityOccurrence(activityOccurrenceId: string, participant: Participant): Promise<Activity> {
    const response = await this._httpClient
      .post<Participant, AxiosResponse<Activity>>(`${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences/${activityOccurrenceId}/participants`,
        participant,
        this.headersWithJsonContentType())
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data as Activity;
  }

  public async getActivityOccurrenceRegistrations(activityOccurrenceId: string): Promise<ActivityRegistration[]> {
    const response = await this._httpClient.get<
      string,
      AxiosResponse<ActivityRegistration[]>
    >(`${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences/${activityOccurrenceId}/registrations`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data as ActivityRegistration[];
  }

  public async deleteActivityRegistration(activityRegistrationId: string): Promise<SucceededOrNotResponse> {
    const response = await this._httpClient.delete<
      string,
      AxiosResponse<SucceededOrNotResponse>
    >(`${process.env.VUE_APP_API_BASE_URL}/api/activity-registrations/${activityRegistrationId}`)
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return new SucceededOrNotResponse(response.status === 204)
  }

  public async transferActivityOccurrenceParticipant(activityOccurrenceId: string, participantId: string): Promise<SucceededOrNotResponse> {
    const response = await this._httpClient
      .post<string, AxiosResponse<SucceededOrNotResponse>>(
        `${process.env.VUE_APP_API_BASE_URL}/api/activity-occurrences/${activityOccurrenceId}/participants/${participantId}`,
        this.buildEmptyBody(),
        this.headersWithJsonContentType())
      .catch(function (error: AxiosError): AxiosResponse<SucceededOrNotResponse> {
        return error.response as AxiosResponse<SucceededOrNotResponse>;
      });
    return response.data;
  }
}

class CreateActivityOccurrenceRequestBody {
  activityId?: string;
  startDate?: string;
  endDate?: string;

  constructor(activityId: string, dates: ActivitySchedule) {
    this.activityId = activityId;
    this.startDate = dates.startDate;
    this.endDate = dates.endDate;
  }
}
